import { useHashParam } from "hooks/use-hash-param"

export const SUBSCRIBER_PROFILE_ID_PARAM = "subscriber_profile_id"

/**
 * Tracks the `subscriber_profile_id` hash param.
 *
 * A hash param, not a query param: `hashchange` is a distinct browser event
 * from `popstate`, so it's unaffected by the app-wide `popstate` handler in
 * `entrypoints/inertia.js` that blocks every other `popstate` listener on
 * `window` (see that file's comment). That handler is intentional, existing
 * behavior for the `UnsavedChanges` feature — not something this feature
 * should work around globally — and a hash param sidesteps it entirely
 * rather than needing to.
 *
 * Single global instance (mounted once in `app-wrapper.jsx`), so reusing
 * `useHashParam` as-is is fine here — no per-row hook-instance concerns like
 * `setSubscriberProfileId` below has.
 */
export function useSubscriberProfileId() {
  const [subscriberProfileId] = useHashParam(SUBSCRIBER_PROFILE_ID_PARAM)
  return subscriberProfileId
}

/**
 * Sets (or clears, when `subscriberId` is falsy) the `subscriber_profile_id`
 * hash param.
 *
 * Not implemented via `useHashParam`'s own setter: this is called from
 * `SubscriberProfileDrawerLink`, rendered once per row, and routing the
 * setter through the hook would mean every row mounts its own unused
 * `useHashParam` state + `hashchange` listener just to reach a setter it
 * never reads from. A plain function avoids that.
 *
 * Opening pushes a new history entry (a plain hash assignment always does),
 * so the browser Back button closes the drawer. Closing replaces the current
 * entry instead — pushing here would leave the "open" entry sitting one step
 * behind the "closed" one, so Back would re-open the drawer instead of
 * leaving the page, and every open/close cycle would add two entries instead
 * of one. `history.replaceState` doesn't fire `hashchange` on its own
 * (unlike a plain `location.hash` assignment), so it's dispatched manually —
 * doing this for both branches, rather than relying on the browser's own
 * dispatch for the open case, makes cross-component propagation (a table row
 * click updating the globally-mounted drawer) deterministic.
 */
export function setSubscriberProfileId(subscriberId) {
  const params = new URLSearchParams(window.location.hash.slice(1))
  const normalizedId = subscriberId ? String(subscriberId) : null

  if (normalizedId) {
    params.set(SUBSCRIBER_PROFILE_ID_PARAM, normalizedId)
  } else {
    params.delete(SUBSCRIBER_PROFILE_ID_PARAM)
  }

  const oldURL = window.location.href
  const url = new URL(window.location.href)
  url.hash = params.toString()

  if (normalizedId) {
    window.location.hash = params.toString()
  } else {
    window.history.replaceState(window.history.state, "", url)
  }

  window.dispatchEvent(
    new HashChangeEvent("hashchange", { oldURL, newURL: url.href })
  )
}
